/**
 * EditRoleModal Component
 *
 * Modal for editing a user's role or an invite request's requested role.
 *
 * Access points:
 * - Action menu on Team page → "Edit Role" for existing users
 * - "Edit Requested Role" button on ReviewInviteModal for pending requests
 *
 * Features:
 * - Role dropdown selection
 * - Jurisdiction selector (shown only when Traffic Viewer role is selected)
 * - Different footer buttons based on context (Update vs Update and Approve)
 *
 * @see Figma design: node-id=1867-161694
 */
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import LytModal from '../ui-core/modal/LytModal';
import LytButton from '../library/button/LytButton';
import LytDropdown from '../library/dropdown/LytDropdown';
import ButtonGroup from '../library/button-group/ButtonGroup';
import {
    applyTypography,
    TYPOGRAPHY_VARIANTS,
} from '../styled/theme/typography-utils';
import { formatRole } from '../../utils/role-utils';
import { userRolesSelector } from '../../routes/users-and-permissions/roles/roles.selectors';
import {
    selectedAgencyJurisdictionsSelector,
    selectedAgencyTypeSelector,
} from '../../store/user-info/user-info.selectors';
import { userRoleSelector } from '../../store/user/user.selectors';
import {
    ROLE_NAMES,
    LEGACY_ROLE_VALUES,
    AGENCY_TYPES,
} from '../../constants/roles.constants';
import {
    buildAssignableRolesList,
    formatRoleOptionLabel,
    TEAM_CONTEXT,
} from './modal-role-options';

// ============================================================================
// CONSTANTS
// ============================================================================

// Role names — sourced from roles.constants.js (single source of truth)
const API_ROLES = ROLE_NAMES;
const LEGACY_ROLES = LEGACY_ROLE_VALUES;

/**
 * Role hint texts
 */
const ROLE_HINTS = {
    [API_ROLES.TRAFFIC_VIEWER]:
        'Can view data only for their assigned jurisdiction below',
    [API_ROLES.TRANSIT_AGENCY_VIEWER]:
        'Can view all agency data but cannot make changes',
    [API_ROLES.TRANSIT_AGENCY_ADMIN]: 'Full access including user management',
    [API_ROLES.TRANSIT_AGENCY_OWNER]: 'Full access with ownership privileges',
    [API_ROLES.EMERGENCY_AGENCY_VIEWER]:
        'Can view all agency data but cannot make changes',
    [API_ROLES.EMERGENCY_AGENCY_ADMIN]: 'Full access including user management',
    [API_ROLES.EMERGENCY_AGENCY_OWNER]: 'Full access with ownership privileges',
    [API_ROLES.LYT_ADMIN]: 'LYT administrator with full system access',
    [API_ROLES.LYT_EMPLOYEE]: 'LYT employee with internal access',
};

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;
const FormRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
`;
const FormLabel = styled.div`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    color: ${({ theme }) => theme?.text?.default};
    width: 120px;
    flex-shrink: 0;
    padding-top: 8px;
`;
const FormField = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
`;
const FieldHint = styled.p`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_0_NORMAL)}
    color: ${({ theme }) => theme?.text?.subtle};
    margin: 4px 0 0 0;
`;
const DropdownWrapper = styled.div`
    /* Custom dropdown styling for modal */
    [class*='dropdown'],
    [class*='Dropdown'] {
        border: 2px solid ${({ theme }) => theme?.colors?.Neutral3 || '#DDDFE5'};
        background-color: ${({ theme }) =>
            theme?.colors?.Neutral0 || '#FFFFFF'};
        border-radius: 4px;
    }
`;
const UnsavedChangesBody = styled.p`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    color: ${({ theme }) => theme?.text?.default};
    margin: 0;
`;
const FooterActions = styled.div`
    display: flex;
    gap: 4px;
    align-items: center;
`;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Checks if a role ID represents the traffic_viewer role
 * @param {string} roleId - The role ID to check
 * @returns {boolean} - True if role is traffic_viewer
 */
const isTrafficViewerRole = (roleId) => {
    return roleId === API_ROLES.TRAFFIC_VIEWER;
};

/**
 * Get role hint text
 * @param {string} roleId - The role ID
 * @returns {string} - Hint text for the role
 */
const getRoleHint = (roleId) => {
    return ROLE_HINTS[roleId] || '';
};

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * EditRoleModal - Modal for editing user or request role
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.closeModal - Function to close modal with result (from ModalProvider)
 * @param {Object} props.user - User object (for editing existing user role)
 * @param {Object} props.request - Request object (for editing pending request role)
 * @param {boolean} props.isRequest - Whether this is editing a request (vs existing user)
 * @param {Function} props.onUpdate - Callback when role is updated (without approval)
 * @param {Function} props.onUpdateAndApprove - Callback when role is updated and request approved
 */
const EditRoleModal = ({
    open = false,
    onClose = undefined,
    closeModal = undefined,
    user = null,
    request = null,
    isRequest = false,
    onUpdate = undefined,
    onUpdateAndApprove = undefined,
    teamContext,
}) => {
    const dispatch = useDispatch();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedJurisdictions, setSelectedJurisdictions] = useState({});
    const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
    const { list: userRolesList, loading: rolesLoading } =
        useSelector(userRolesSelector);
    const { list: userJurisdictions, loading: jurisdictionsLoading } =
        useSelector(selectedAgencyJurisdictionsSelector);
    const currentUserRole = useSelector(userRoleSelector);
    const agencyType = useSelector(selectedAgencyTypeSelector);

    // Determine the entity being edited
    const entity = isRequest ? request : user;
    const entityName = isRequest
        ? request?.email || request?.inviteeEmail || 'User'
        : user?.firstname && user?.lastname
        ? `${user.firstname} ${user.lastname}`
        : user?.email || 'User';

    // Get current role from entity (uses normalized field names)
    const currentRole = isRequest
        ? request?.role || request?.assignedRole
        : user?.usertype || user?.role;

    // Get current jurisdictions from entity (memoized to prevent infinite re-renders)
    const currentJurisdictions = useMemo(
        () => entity?.jurisdictions || [],
        [entity?.jurisdictions]
    );

    // Check if traffic_viewer is selected (jurisdiction assignment only for this role)
    const isTrafficViewerSelected = isTrafficViewerRole(selectedRole?.id);

    // Track whether the user has made changes from the initial values
    const isDirty = useMemo(() => {
        if (!selectedRole) return false;

        // Check if role changed
        if (selectedRole.id !== currentRole) return true;

        // Check if jurisdictions changed (only relevant for traffic_viewer)
        if (isTrafficViewerSelected) {
            const currentIds = new Set(currentJurisdictions.map((j) => j.id));
            const selectedIds = new Set(Object.keys(selectedJurisdictions));
            if (currentIds.size !== selectedIds.size) return true;
            for (const id of currentIds) {
                if (!selectedIds.has(id)) return true;
            }
        }
        return false;
    }, [
        selectedRole,
        currentRole,
        isTrafficViewerSelected,
        selectedJurisdictions,
        currentJurisdictions,
    ]);

    // Transform jurisdictions for dropdown
    const jurisdictionOptions = useMemo(
        () =>
            (userJurisdictions || []).map((jurisdiction) => ({
                id: jurisdiction.id,
                text: jurisdiction.name,
            })),
        [userJurisdictions]
    );

    // Initialize selected role and jurisdictions when modal opens or entity changes
    useEffect(() => {
        if (open && currentRole) {
            const roleOption = {
                id: currentRole,
                text: formatRole(currentRole),
            };
            setSelectedRole(roleOption);

            // Initialize jurisdictions
            if (currentJurisdictions.length > 0) {
                const jurisdictionMap = {};
                currentJurisdictions.forEach((j) => {
                    jurisdictionMap[j.id] = true;
                });
                setSelectedJurisdictions(jurisdictionMap);
            } else {
                setSelectedJurisdictions({});
            }
        }
    }, [open, currentRole, currentJurisdictions]);
    if (!open) return null;

    // Build modal title
    const modalTitle = isRequest ? 'Edit Requested Role' : `Edit ${entityName}`;

    // Role hint text
    const roleHint = selectedRole ? getRoleHint(selectedRole.id) : '';

    /**
     * Create roles list based on current user's permissions and agency type.
     * Mirrors the same logic used in InviteUserModal for consistency.
     */
    const createRolesList = () => {
        // Edit role is only used on agency team / review-invite flows; default to agency context.
        const resolvedTeamContext = teamContext ?? TEAM_CONTEXT.AGENCY;
        const allowedRoles = buildAssignableRolesList({
            userRolesList,
            currentUserRole,
            teamContext: resolvedTeamContext,
            agencyType,
        });
        return allowedRoles.map((role) => ({
            id: role.role_name,
            text: formatRoleOptionLabel(role.role_name, resolvedTeamContext),
        }));
    };
    const rolesForSelect = createRolesList();

    /**
     * Handle role selection change
     */
    const handleRoleChange = (selected) => {
        if (selected && Object.keys(selected).length > 0) {
            const selectedId = Object.keys(selected)[0];
            const role = rolesForSelect.find(
                (r) => String(r.id) === String(selectedId)
            );
            setSelectedRole(role);

            // Clear jurisdictions if not traffic_viewer
            if (!isTrafficViewerRole(selectedId)) {
                setSelectedJurisdictions({});
            }
        }
    };

    /**
     * Attempt to dismiss — if dirty AND in the review-invite flow, show
     * the unsaved-changes confirmation; otherwise close immediately.
     * The confirmation is skipped on the Team page (isRequest === false)
     * because that context does not need it.
     */
    const attemptDismiss = (result) => {
        if (isRequest && isDirty) {
            setShowUnsavedConfirm(true);
            return;
        }
        closeModal?.(result) || onClose?.();
    };

    /**
     * Handle close action (X button / overlay)
     */
    const handleClose = () => {
        attemptDismiss({
            dismissed: true,
        });
    };

    /**
     * Handle cancel action
     */
    const handleCancel = () => {
        attemptDismiss({
            cancelled: true,
        });
    };

    /**
     * Discard changes and close the modal
     */
    const handleDiscardChanges = () => {
        setShowUnsavedConfirm(false);
        closeModal?.({
            cancelled: true,
        }) || onClose?.();
    };

    /**
     * Keep editing — dismiss the confirmation dialog
     */
    const handleKeepEditing = () => {
        setShowUnsavedConfirm(false);
    };

    /**
     * Handle update action (for existing users)
     */
    const handleUpdate = async () => {
        setIsSubmitting(true);
        try {
            const updateData = {
                role: selectedRole?.id,
                jurisdictions: isTrafficViewerSelected
                    ? Object.keys(selectedJurisdictions)
                    : [],
            };
            if (onUpdate) {
                await onUpdate(entity, updateData);
            }
            closeModal?.({
                action: 'updated',
                entity,
                newRole: selectedRole?.id,
                jurisdictions: updateData.jurisdictions,
            });
        } catch (error) {
            console.error('Error updating role:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    /**
     * Handle update and approve action (for pending requests)
     */
    const handleUpdateAndApprove = async () => {
        setIsSubmitting(true);
        try {
            const updateData = {
                role: selectedRole?.id,
                jurisdictions: isTrafficViewerSelected
                    ? Object.keys(selectedJurisdictions)
                    : [],
            };
            if (onUpdateAndApprove) {
                await onUpdateAndApprove(entity, updateData);
            }
            closeModal?.({
                action: 'updated_and_approved',
                entity,
                newRole: selectedRole?.id,
                jurisdictions: updateData.jurisdictions,
            });
        } catch (error) {
            console.error('Error updating and approving:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Determine if update button should be disabled
    const isUpdateDisabled =
        !selectedRole ||
        (isTrafficViewerSelected &&
            Object.keys(selectedJurisdictions).length === 0);

    // Footer actions based on context
    const footerActions = (
        <FooterActions>
            <ButtonGroup>
                <LytButton
                    type="tertiary"
                    onClick={handleCancel}
                    disabled={isSubmitting}>
                    Cancel
                </LytButton>
                {isRequest ? (
                    <LytButton
                        type="primary"
                        onClick={handleUpdateAndApprove}
                        disabled={isUpdateDisabled || isSubmitting}
                        loading={isSubmitting}>
                        Update and Approve
                    </LytButton>
                ) : (
                    <LytButton
                        type="primary"
                        onClick={handleUpdate}
                        disabled={isUpdateDisabled || isSubmitting}
                        loading={isSubmitting}>
                        Update
                    </LytButton>
                )}
            </ButtonGroup>
        </FooterActions>
    );

    // Unsaved changes confirmation footer
    const unsavedConfirmActions = (
        <FooterActions>
            <ButtonGroup>
                <LytButton type="tertiary" onClick={handleKeepEditing}>
                    Keep editing
                </LytButton>
                <LytButton type="primary" onClick={handleDiscardChanges}>
                    Discard Changes
                </LytButton>
            </ButtonGroup>
        </FooterActions>
    );
    return (
        <>
            <LytModal
                open={open}
                title={modalTitle}
                onClose={handleClose}
                actions={footerActions}
                size="medium">
                <ModalContent>
                    {/* Role Selection */}
                    <FormRow>
                        <FormLabel>Role</FormLabel>
                        <FormField>
                            <DropdownWrapper>
                                <LytDropdown
                                    id="edit-role-dropdown"
                                    name="role"
                                    dropdownPlaceholder={
                                        rolesLoading
                                            ? 'Loading roles...'
                                            : 'Select Role'
                                    }
                                    options={rolesForSelect}
                                    selectedOptions={
                                        selectedRole
                                            ? {
                                                  [selectedRole.id]: true,
                                              }
                                            : {}
                                    }
                                    onChange={handleRoleChange}
                                    loading={rolesLoading}
                                    fullWidth
                                    usePortal
                                />
                            </DropdownWrapper>
                            {roleHint && <FieldHint>{roleHint}</FieldHint>}
                        </FormField>
                    </FormRow>

                    {/* Jurisdiction Selection - Only shown for traffic_viewer */}
                    {isTrafficViewerSelected && (
                        <FormRow>
                            <FormLabel>Jurisdiction</FormLabel>
                            <FormField>
                                <DropdownWrapper>
                                    <LytDropdown
                                        id="edit-jurisdiction-dropdown"
                                        name="jurisdiction"
                                        dropdownPlaceholder={
                                            jurisdictionsLoading
                                                ? 'Loading jurisdictions...'
                                                : 'Select Jurisdiction(s)'
                                        }
                                        options={jurisdictionOptions}
                                        selectedOptions={selectedJurisdictions}
                                        onChange={(selected) =>
                                            setSelectedJurisdictions(selected)
                                        }
                                        loading={jurisdictionsLoading}
                                        multiselect
                                        showSelectionIndicator
                                        fullWidth
                                        usePortal
                                    />
                                </DropdownWrapper>
                                <FieldHint>
                                    Select the jurisdiction the traffic viewer
                                    can access
                                </FieldHint>
                            </FormField>
                        </FormRow>
                    )}
                </ModalContent>
            </LytModal>

            {/* Unsaved Changes Confirmation Modal */}
            <LytModal
                open={showUnsavedConfirm}
                title="Unsaved Changes"
                onClose={handleKeepEditing}
                actions={unsavedConfirmActions}
                size="small">
                <UnsavedChangesBody>
                    You&apos;ve made changes to this role that haven&apos;t been
                    saved. If you close now, your changes will be lost.
                </UnsavedChangesBody>
            </LytModal>
        </>
    );
};
EditRoleModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    closeModal: PropTypes.func,
    user: PropTypes.shape({
        user_id: PropTypes.string,
        email: PropTypes.string,
        firstname: PropTypes.string,
        lastname: PropTypes.string,
        usertype: PropTypes.string,
        role: PropTypes.string,
        jurisdictions: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                name: PropTypes.string,
            })
        ),
    }),
    request: PropTypes.shape({
        request_id: PropTypes.string,
        inviteeEmail: PropTypes.string,
        invitee_email: PropTypes.string,
        assignedRole: PropTypes.string,
        assigned_role: PropTypes.string,
        requested_role: PropTypes.string,
        jurisdictions: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                name: PropTypes.string,
            })
        ),
    }),
    isRequest: PropTypes.bool,
    onUpdate: PropTypes.func,
    onUpdateAndApprove: PropTypes.func,
};
export default EditRoleModal;
